import { useEffect, useState } from "react";
import type { TravelDetailData } from "../types/travelDetail";
import { getMapUrls } from "../utils/map";
import { buildHeroMetaItems } from "../utils/travelMeta";

type TravelDetailPageProps = {
  data: TravelDetailData;
  onSelectAttraction?: (id: string) => void;
  onBack?: () => void;
  onBook?: () => void;
};

export default function TravelDetailPage({
  data,
  onSelectAttraction,
  onBack,
  onBook
}: TravelDetailPageProps) {
  const overview = data.sections.overview?.enabled ? data.sections.overview : undefined;
  const itinerary = data.sections.itinerary?.enabled
    ? data.sections.itinerary
    : undefined;
  const inclusions = data.sections.inclusions?.enabled
    ? data.sections.inclusions
    : undefined;
  const mapSection = data.sections.map?.enabled ? data.sections.map : undefined;
  const gallery = data.sections.gallery?.enabled ? data.sections.gallery : undefined;
  const relatedTrips = data.sections.relatedTrips?.enabled
    ? data.sections.relatedTrips
    : undefined;
  const bookingCard = data.sections.bookingCard?.enabled
    ? data.sections.bookingCard
    : undefined;

  const itineraryDays = itinerary?.days ?? [];
  const galleryItems = gallery?.images ?? [];
  const metaItems = overview ? buildHeroMetaItems(data) : [];
  const depositValue = data.basic.depositAmount ?? data.basic.priceFrom;

  const [openDayId, setOpenDayId] = useState(itineraryDays[0]?.id ?? "");
  const [galleryIndex, setGalleryIndex] = useState(0);
  const mapUrls = getMapUrls(mapSection?.embedUrl);

  useEffect(() => {
    setOpenDayId(itinerary?.days[0]?.id ?? "");
    setGalleryIndex(0);
  }, [data.id, itinerary]);

  const handleGalleryPrev = () => {
    if (galleryItems.length === 0) {
      return;
    }
    setGalleryIndex((current) =>
      current === 0 ? galleryItems.length - 1 : current - 1
    );
  };

  const handleGalleryNext = () => {
    if (galleryItems.length === 0) {
      return;
    }
    setGalleryIndex((current) => (current + 1) % galleryItems.length);
  };

  return (
    <div className="bg-[#f5f8fb] pb-16 text-[#111827]">
      <section className="relative overflow-hidden bg-[#0b111a]">
        <div
          className="h-[340px] w-full bg-cover bg-center md:h-[420px]"
          style={{ backgroundImage: `url('${data.basic.heroImageUrl}')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(6,12,20,0.78)] via-[rgba(6,12,20,0.55)] to-[rgba(6,12,20,0.2)]" />
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
            <div className="max-w-2xl text-white">
              <p className="text-xs uppercase tracking-[0.3em] text-white/70">
                {data.basic.destination}
              </p>
              <h1 className="mt-4 text-3xl font-semibold leading-tight md:text-5xl">
                {data.basic.title}
              </h1>
              {data.basic.summary && (
                <p className="mt-4 text-sm leading-relaxed text-white/80 md:text-base">
                  {data.basic.summary}
                </p>
              )}
              {metaItems.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-3 text-xs text-white/85">
                  {metaItems.map((item) => (
                    <span
                      key={item.label}
                      className="rounded-full bg-white/10 px-4 py-2"
                    >
                      <span className="text-white/60">{item.label}</span>{" "}
                      <span className="font-semibold text-white">
                        {item.value}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1140px] px-4 pt-10 md:px-8 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_0.6fr] lg:items-start">
          <div className="space-y-10">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-2 rounded-full bg-[#0b1c2b] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#0d5be1]"
              >
                <span aria-hidden="true">&lt;</span>
                Back to list
              </button>
            )}

            {overview && (
              <div className="bg-transparent p-0">
                {overview.eyebrow && (
                  <p className="text-xs uppercase tracking-[0.34em] text-[#f7b500]">
                    {overview.eyebrow}
                  </p>
                )}
                {overview.title && (
                  <h2 className="mt-3 text-2xl font-semibold text-[#111827] md:text-3xl">
                    {overview.title}
                  </h2>
                )}
                <div className="mt-5 space-y-4 text-sm leading-relaxed text-[#5f6b7a] md:text-base">
                  {overview.paragraphs.map((paragraph, index) => (
                    <p key={`${data.id}-overview-${index}`}>{paragraph}</p>
                  ))}
                </div>

                {overview.highlights && overview.highlights.length > 0 && (
                  <div className="mt-8 grid gap-4 md:grid-cols-2">
                    {overview.highlights.map((highlight) => (
                      <div
                        key={highlight.title}
                        className="rounded-2xl border border-[#e5e7eb] bg-transparent p-4"
                      >
                        <p className="text-sm font-semibold text-[#111827]">
                          {highlight.title}
                        </p>
                        <p className="mt-2 text-xs text-[#6b7280]">
                          {highlight.description}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {itinerary && (
              <div className="bg-transparent p-0">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    {itinerary.eyebrow && (
                      <p className="text-xs uppercase tracking-[0.34em] text-[#f7b500]">
                        {itinerary.eyebrow}
                      </p>
                    )}
                    {itinerary.title && (
                      <h3 className="mt-2 text-xl font-semibold text-[#111827]">
                        {itinerary.title}
                      </h3>
                    )}
                  </div>
                  {itinerary.subtitle && (
                    <span className="text-xs uppercase tracking-[0.2em] text-[#9aa3af]">
                      {itinerary.subtitle}
                    </span>
                  )}
                </div>

                <div className="mt-6 divide-y divide-[#e5e7eb]">
                  {itinerary.days.map((day) => {
                    const isOpen = openDayId === day.id;
                    return (
                      <div key={day.id} className="py-4">
                        <button
                          type="button"
                          onClick={() =>
                            setOpenDayId((current) =>
                              current === day.id ? "" : day.id
                            )
                          }
                          className="flex w-full items-center justify-between gap-4 text-left"
                        >
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#f7b500]">
                              {day.dayLabel}
                            </p>
                            <p className="mt-1 text-sm font-semibold text-[#111827]">
                              {day.title}
                            </p>
                          </div>
                          <span className="text-lg text-[#9aa3af]">
                            {isOpen ? "-" : "+"}
                          </span>
                        </button>

                        {isOpen && (
                          <div className="mt-4 space-y-4 text-sm text-[#5f6b7a]">
                            {day.location && (
                              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9aa3af]">
                                {day.location}
                              </p>
                            )}
                            {day.paragraphs.map((text, index) => (
                              <p key={`${day.id}-paragraph-${index}`}>{text}</p>
                            ))}

                            {day.highlights && day.highlights.length > 0 && (
                              <ul className="space-y-2">
                                {day.highlights.map((item) => (
                                  <li key={item} className="flex gap-2">
                                    <span className="text-[#f7b500]">*</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            )}

                            {day.notes && day.notes.length > 0 && (
                              <div className="rounded-2xl border border-[#e5e7eb] bg-transparent p-4 text-xs text-[#6b7280]">
                                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9aa3af]">
                                  {day.notesTitle ?? "Planning Note"}
                                </p>
                                <ul className="mt-3 space-y-2">
                                  {day.notes.map((note) => (
                                    <li key={note} className="flex gap-2">
                                      <span className="text-[#0d5be1]">-</span>
                                      <span>{note}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {inclusions && (
              <div className="grid gap-6 md:grid-cols-2">
                <div className="bg-transparent p-0">
                  <h3 className="text-lg font-semibold text-[#111827]">
                    {inclusions.includedTitle ?? "What's Included"}
                  </h3>
                  <ul className="mt-4 space-y-3 text-sm text-[#5f6b7a]">
                    {inclusions.included.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#f7b500]/15 text-[#f7b500]">
                          +
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-transparent p-0">
                  <h3 className="text-lg font-semibold text-[#111827]">
                    {inclusions.excludedTitle ?? "What's Not Included"}
                  </h3>
                  <ul className="mt-4 space-y-3 text-sm text-[#5f6b7a]">
                    {inclusions.excluded.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#f87171]/15 text-[#ef4444]">
                          x
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {mapSection && (
              <div className="bg-transparent p-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-[#111827]">
                    {mapSection.title ?? "Route Map"}
                  </h3>
                  {mapSection.badge && (
                    <span className="text-xs uppercase tracking-[0.2em] text-[#9aa3af]">
                      {mapSection.badge}
                    </span>
                  )}
                </div>
                <div className="mt-4 overflow-hidden rounded-2xl border border-[#e5e7eb] bg-transparent">
                  {mapUrls.embedUrl ? (
                    <>
                      <iframe
                        src={mapUrls.embedUrl}
                        className="h-[320px] w-full"
                        loading="lazy"
                        title={`${data.basic.destination} map`}
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                      {mapUrls.openUrl && (
                        <div className="border-t border-[#e5e7eb] bg-white px-4 py-3 text-right">
                          <a
                            href={mapUrls.openUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-semibold uppercase tracking-[0.16em] text-[#0d5be1]"
                          >
                            Open in Google Maps
                          </a>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex h-[320px] items-center justify-center text-sm text-[#6b7280]">
                      Map link unavailable
                    </div>
                  )}
                </div>
              </div>
            )}

            {gallery && (
              <div className="bg-transparent p-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-[#111827]">
                    {gallery.title ?? "Gallery"}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-[#6b7280]">
                    <button
                      type="button"
                      onClick={handleGalleryPrev}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-[#e5e7eb] bg-white text-sm text-[#111827] transition hover:-translate-y-0.5"
                      aria-label="Previous image"
                    >
                      &lt;
                    </button>
                    <span>
                      {galleryItems.length === 0 ? 0 : galleryIndex + 1}/
                      {galleryItems.length}
                    </span>
                    <button
                      type="button"
                      onClick={handleGalleryNext}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-[#e5e7eb] bg-white text-sm text-[#111827] transition hover:-translate-y-0.5"
                      aria-label="Next image"
                    >
                      &gt;
                    </button>
                  </div>
                </div>

                <div className="mt-4 overflow-hidden rounded-3xl border border-[#e5e7eb]">
                  <div
                    className="flex transition-transform duration-500"
                    style={{ transform: `translateX(-${galleryIndex * 100}%)` }}
                  >
                    {galleryItems.map((item) => (
                      <div key={item.url} className="min-w-full">
                        <img
                          src={item.url}
                          alt={item.alt}
                          className="h-[320px] w-full object-cover md:h-[380px]"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {relatedTrips && relatedTrips.items.length > 0 && (
              <div className="bg-transparent p-0">
                <h3 className="text-lg font-semibold text-[#111827]">
                  {relatedTrips.title ?? "More Attractions"}
                </h3>
                <div className="mt-5 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {relatedTrips.items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => onSelectAttraction?.(item.id)}
                      className="overflow-hidden rounded-2xl border border-[#eef2f6] bg-white text-left shadow-[0_12px_26px_rgba(15,23,36,0.08)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_30px_rgba(15,23,36,0.12)]"
                    >
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="h-[160px] w-full object-cover"
                      />
                      <div className="space-y-2 px-4 py-4">
                        <p className="text-xs uppercase tracking-[0.28em] text-[#9aa3af]">
                          {item.durationLabel}
                        </p>
                        <h4 className="text-sm font-semibold text-[#111827]">
                          {item.title}
                        </h4>
                        <p className="text-xs text-[#9aa3af]">{item.country}</p>
                        <p className="text-sm font-semibold text-[#111827]">
                          {item.priceFrom}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {bookingCard && (
            <aside className="space-y-6 lg:sticky lg:top-24">
              <div className="rounded-3xl bg-white p-6 shadow-[0_18px_40px_rgba(15,23,36,0.12)]">
                <p className="text-xs uppercase tracking-[0.3em] text-[#f7b500]">
                  {bookingCard.title ?? "Pricing and Booking"}
                </p>
                {bookingCard.subtitle && (
                  <p className="mt-2 text-sm text-[#6b7280]">
                    {bookingCard.subtitle}
                  </p>
                )}
                <div className="mt-5 rounded-2xl bg-[#0b1c2b] px-5 py-4 text-white">
                  <p className="text-xs uppercase tracking-[0.3em] text-white/70">
                    {bookingCard.priceLabel ?? "From"}
                  </p>
                  <p className="mt-2 text-2xl font-semibold">
                    {data.basic.priceFrom}
                  </p>
                  <div className="mt-4 border-t border-white/20 pt-4">
                    <p className="text-xs uppercase tracking-[0.3em] text-white/70">
                      {bookingCard.depositTitle ?? "Deposit"}
                    </p>
                    <p className="mt-2 text-xl font-semibold">{depositValue}</p>
                    {bookingCard.depositNote && (
                      <p className="mt-2 text-xs text-white/70">
                        {bookingCard.depositNote}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onBook}
                  className="mt-6 w-full rounded-xl bg-[#f7b500] px-5 py-3 text-xs font-semibold text-white shadow-[0_12px_22px_rgba(247,181,0,0.35)]"
                >
                  {bookingCard.ctaLabel ?? "BOOK NOW"}
                </button>
              </div>
            </aside>
          )}
        </div>
      </section>
    </div>
  );
}
